import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, Store, X, Star } from "lucide-react";
import API from "../api";
import ProductCard from "../components/ProductCard";

const CATEGORIES_LIST = [
  { slug: "electronique", name: "Électronique" },
  { slug: "mode-vetements", name: "Mode & Vêtements" },
  { slug: "alimentation", name: "Alimentation" },
  { slug: "maison-decoration", name: "Maison & Déco" },
  { slug: "beaute-sante", name: "Beauté & Santé" },
  { slug: "sports-loisirs", name: "Sports & Loisirs" },
  { slug: "services", name: "Services" },
  { slug: "autres", name: "Autres" },
];

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dbCategories, setDbCategories] = useState([]);
  const [vendors, setVendors] = useState([]);

  const search = searchParams.get("search") || "";
  const category = searchParams.get("category") || "";
  const minPrice = searchParams.get("min") || "";
  const maxPrice = searchParams.get("max") || "";
  const vendorId = searchParams.get("vendor_id") || "";

  const [searchInput, setSearchInput] = useState(search);
  const [minInput, setMinInput] = useState(minPrice);
  const [maxInput, setMaxInput] = useState(maxPrice);
  const [vendorInput, setVendorInput] = useState(vendorId);

  useEffect(() => {
    API.get("/api/categories").then((r) => setDbCategories(r.data)).catch(() => {});
    API.get("/api/vendors/approved/list").then((r) => setVendors(r.data || [])).catch(() => {});
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set("search", search);
      if (category) {
        const cat = dbCategories.find((c) => c.slug === category);
        if (cat) params.set("category_id", cat._id);
      }
      if (minPrice) params.set("min_price", minPrice);
      if (maxPrice) params.set("max_price", maxPrice);
      if (vendorId) params.set("vendor_id", vendorId);
      const { data } = await API.get(`/api/products?${params}`);
      setProducts(data.products || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, category, minPrice, maxPrice, vendorId, page, dbCategories]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const applySearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(searchParams);
    if (searchInput) p.set("search", searchInput); else p.delete("search");
    if (minInput) p.set("min", minInput); else p.delete("min");
    if (maxInput) p.set("max", maxInput); else p.delete("max");
    if (vendorInput) p.set("vendor_id", vendorInput); else p.delete("vendor_id");
    p.delete("page");
    setSearchParams(p);
    setPage(1);
    setFiltersOpen(false);
  };

  const setCategory = (slug) => {
    const p = new URLSearchParams(searchParams);
    if (slug) p.set("category", slug); else p.delete("category");
    p.delete("page");
    setSearchParams(p);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchInput(""); setMinInput(""); setMaxInput(""); setVendorInput("");
    setPage(1);
  };

  const selectedVendor = vendors.find((v) => v.user_id === vendorId);
  const totalPages = Math.ceil(total / 20);
  const hasFilters = search || category || minPrice || maxPrice || vendorId;

  return (
    <div className="bg-[#F9FAFB] min-h-screen pb-24 md:pb-8">
      <div className="w-full max-w-7xl mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-extrabold text-[#232F3E]">
              {selectedVendor ? `Boutique: ${selectedVendor.shop_name}` :
               category ? CATEGORIES_LIST.find((c) => c.slug === category)?.name || "Catalogue" :
               search ? `"${search}"` : "Tous les produits"}
            </h1>
            <p className="text-sm text-gray-500">{total} produit{total > 1 ? "s" : ""} trouvé{total > 1 ? "s" : ""}</p>
          </div>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="flex items-center gap-2 bg-white border border-gray-200 text-sm font-medium px-3 py-2 rounded-md hover:border-[#FF9900] transition-colors"
            data-testid="filters-toggle"
          >
            <SlidersHorizontal size={15} /> Filtres
            {hasFilters && <span className="w-2 h-2 bg-[#FF9900] rounded-full" />}
          </button>
        </div>

        {/* Active vendor filter badge */}
        {selectedVendor && (
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full px-3 py-1.5 text-sm">
              <Store size={13} className="text-blue-600" />
              <span className="font-semibold text-blue-700">{selectedVendor.shop_name}</span>
              {selectedVendor.rating > 0 && (
                <span className="flex items-center gap-0.5 text-xs text-gray-500">
                  <Star size={10} className="text-[#FF9900] fill-[#FF9900]" /> {selectedVendor.rating}
                </span>
              )}
              <button onClick={() => { const p = new URLSearchParams(searchParams); p.delete("vendor_id"); setSearchParams(p); setVendorInput(""); }} className="ml-1 text-blue-400 hover:text-blue-600">
                <X size={13} />
              </button>
            </div>
          </div>
        )}

        {/* Filters panel */}
        {filtersOpen && (
          <div className="bg-white border border-gray-200 rounded-md p-4 mb-4 shadow-sm">
            <form onSubmit={applySearch} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1 block">Recherche</label>
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Mot-clé..."
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none"
                      data-testid="catalog-search-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1 block">Boutique</label>
                  <select
                    value={vendorInput}
                    onChange={(e) => setVendorInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none bg-white"
                    data-testid="vendor-filter-select"
                  >
                    <option value="">Toutes les boutiques</option>
                    {vendors.map((v) => (
                      <option key={v.user_id} value={v.user_id}>{v.shop_name}{v.rating > 0 ? ` ⭐ ${v.rating}` : ""}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1 block">Prix min (FCFA)</label>
                  <input
                    type="number"
                    value={minInput}
                    onChange={(e) => setMinInput(e.target.value)}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none"
                    data-testid="min-price-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1 block">Prix max (FCFA)</label>
                  <input
                    type="number"
                    value={maxInput}
                    onChange={(e) => setMaxInput(e.target.value)}
                    placeholder="1000000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#FF9900] outline-none"
                    data-testid="max-price-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="bg-[#FF9900] text-[#111827] font-bold px-4 py-2 rounded-md text-sm hover:bg-[#E68A00] transition-colors" data-testid="apply-filters-btn">
                  Appliquer
                </button>
                <button type="button" onClick={clearFilters} className="border border-gray-300 text-gray-600 font-medium px-4 py-2 rounded-md text-sm hover:bg-gray-50">
                  Réinitialiser
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Category pills */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
          <button
            onClick={() => setCategory("")}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${!category ? "bg-[#232F3E] text-white border-[#232F3E]" : "bg-white border-gray-300 text-gray-600 hover:border-[#232F3E]"}`}
            data-testid="category-all"
          >
            Tout
          </button>
          {CATEGORIES_LIST.map(({ slug, name }) => (
            <button
              key={slug}
              onClick={() => setCategory(slug)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${category === slug ? "bg-[#232F3E] text-white border-[#232F3E]" : "bg-white border-gray-300 text-gray-600 hover:border-[#232F3E]"}`}
              data-testid={`category-pill-${slug}`}
            >
              {name}
            </button>
          ))}
        </div>

        {/* Products grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="bg-white rounded-md border border-gray-200 overflow-hidden animate-pulse">
                <div className="aspect-square bg-gray-200" />
                <div className="p-2.5 space-y-2">
                  <div className="h-3 bg-gray-200 rounded" />
                  <div className="h-5 bg-gray-200 rounded w-2/3" />
                  <div className="h-7 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-md border border-gray-200">
            <Search size={48} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">Aucun produit trouvé</p>
            {hasFilters && (
              <button onClick={clearFilters} className="mt-3 text-[#FF9900] font-medium text-sm hover:underline">
                Effacer les filtres
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {products.map((p) => (
              <ProductCard key={p._id} product={p} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-40 hover:bg-gray-50">
              Précédent
            </button>
            <span className="px-3 py-1.5 text-sm text-gray-600">Page {page} / {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-40 hover:bg-gray-50">
              Suivant
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
